import React, { useEffect, useId, useRef, useState } from 'react';

import styles from './Dashboard.module.scss';
import stylesLayout from '../layouters/Layouters.module.scss';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TpAnalyticsDataRefs, TpDataContext } from '../../../data/interfaces';
import { BlockingOpenedItem } from '../../../components/blocking-opened-item/BlockingOpenedItem';
import {
  TpChartDefinition,
  TpDashboardContext,
  TpDashboardCreationContext,
  TpDashboardDefinition,
  TpDashboardElement,
  isChartDefinition,
} from './interfaces';
import { useAppDispatch, useAppSelector, useUser } from '../../../app/hooks';
import {
  addDashboard,
  addNewElementToSection,
  findDashboardElements,
  selectDashboardDefs,
  updateChartCompoundAspectBinding,
} from '../data/dashboardsSlice';

import {
  addNewChartByUser,
  updateChartAspectBinding,
  updateChartFilter,
  updateChartConfigParam,
} from '../data/dashboardsSlice';
import { getLayoutInHorGroupRelative } from './layout-hints';
//import { promptAddDashboardSection } from './edit-mode/sectionTools';
import { SingleItemView } from '../single-item-view/SingleItemView';
import { promptAddElement } from './edit-mode/generalTools';
import { renderDashboardElement } from './render-elem';
import { selectAnalyticsData, setDataContext } from '../../../data/analyticsDataSlice';
import { TpAssetInfo } from '../../../data/assets/interfaces';
import { buildDataContext } from '../../../data/datacontextBuilder';
import { useApiContext } from '../../../api';
import TopBar from './TopBar';
import { analyticsDataStorage, updateDataContextAll } from '../../../data/analyticsDataStorage';
import { selectTemplatesAndCombinedOntology } from '../../../data/templateSlice';
import { showErrorMessage } from '../../../components/message-boxes/ErrorMessage';
import { fetchLazyColumnData } from '../../../data/mutation-ledger/asyncColumnFetch';
import { TpSectionsIndexContext } from '../../../components/sections-index/SectionsIndex';

import TemplateEditor from '../../../components/yaml-editor/TemplateEditor';
import schemaDashboardTemplate from '../../../json-schemas/schemaDashboardTemplate';
import DataContextSchema from './DataContextSchema';
import { augmentDataContext } from '../../../data/augment-datacontext';
import { validateDataContext } from '../../../data/validateDataContext';
import { useEventNotification } from '../hooks/events';
import { DASHBOARD_ACTION_TRIGGERIDS, executeDashboardTriggers } from '../actions/executeActionTriggers';
import { useLoadViewState } from '../hooks/useLoadViewState';
import { VIEW_STATE_QUERY_PARAM, VIEW_STATE_TERMS } from '../../../data/snapshot/dashboardStateSerialization';
import { TpActiveEnSelectionFilter } from '../../../data/selection/interfaces';
import { createDashboardFromTemplate } from '../data/createDashboardFromTemplate';
import type { TpSectionElementDTO } from '../../../data/dashboard-template/schemas';
import { useSearchParams } from 'react-router-dom';
import { useActiveDashboardTemplate } from '../hooks/useActiveDashboardTemplate';
import { useSelectDashboardTemplate } from '../hooks/useSelectDashboardTemplate';
import { getAssetTemplateId } from '../../../data/templateSlice.helpers';

interface TpDashboardProps {
  dashboardtemplate: any;
  assetInfo: TpAssetInfo;
  sectionIndexContext: TpSectionsIndexContext;
  isAssetTemplate?: boolean;
  onTemplateUpdate?: (templateString: string) => void;
  onTemplateSave?: (templateString: string) => Promise<string>;
}

function Dashboard({
  dashboardtemplate,
  assetInfo,
  sectionIndexContext,
  isAssetTemplate,
  onTemplateUpdate,
  onTemplateSave,
}: TpDashboardProps) {
  const dispatch = useAppDispatch();
  const api = useApiContext();
  const { loadViewState } = useLoadViewState();
  const assetId = assetInfo.id;
  const { user } = useUser();
  const [searchParams] = useSearchParams();

  const dashboardInstanceId = useId();

  const analyticsDataRefs: TpAnalyticsDataRefs = useAppSelector(selectAnalyticsData);
  const dashboards = useAppSelector(selectDashboardDefs).dashboardDefs;
  const dashboardDef: TpDashboardDefinition = dashboards[assetId];
  const { ontology } = useAppSelector(selectTemplatesAndCombinedOntology);
  const activeDashboardTemplate = useActiveDashboardTemplate({ assetInfo, isAssetTemplate });
  const selectDashboardTemplate = useSelectDashboardTemplate();

  const storedDashboardTemplate = dashboardDef?.templateInfo ?? activeDashboardTemplate;

  const [dataContextIsReady, setDataContextIsReady] = useState(false);
  const [dashboardIsReady, setDashboardIsReady] = useState(false);
  const [dataContext, setDataContextState] = useState<TpDataContext>({} as TpDataContext);
  const [dashboardContext, setDashboardContext] = useState<TpDashboardContext>({} as TpDashboardContext);
  const [showSpinner, setShowSpinner] = useState(true);

  const [templateError, setTemplateError] = useState<string | null>(null);

  const bottomElement = useRef<HTMLDivElement | null>(null);
  const openedItemRef = useRef<HTMLDivElement | null>(null);
  const [openedItem, setOpenedItem] = useState<{ canonicalTypeId: string; itemId: string } | null>(null);

  const [editMode, setEditMode] = useState(false);

  const showMissingContentPlaceholder =
    dashboardtemplate.onMissingContent && !dashboardtemplate.onMissingContent.showError;

  const handleOpenItem = (canonicalTypeId: string, itemId: string) => {
    setOpenedItem({ canonicalTypeId, itemId });
  };

  function handleCloseItem() {
    setOpenedItem(null);
  }

  async function initViewState(dc: TpDataContext) {
    // Obtain current state ID from the URL.
    const viewStateId = searchParams.get(VIEW_STATE_QUERY_PARAM);
    if (!viewStateId) return;

    loadViewState({
      stateId: viewStateId,
      assetInfo,
      dataContext: dc,
      currentTemplateId: storedDashboardTemplate?.id ?? '',
    }).catch((error) => {
      console.error('Failed to load view state:', error);
      showErrorMessage(
        `Failed to load ${VIEW_STATE_TERMS.singular}. Please contact support in case the issue persists.`,
      );
    });
  }

  function addChart(chartDef: TpChartDefinition, dashboardDefId: string, targetSectionId?: string) {
    dispatch(
      addNewChartByUser({
        dashboardDefId,
        chartDef: {
          // If not specified, a chart will be added at 50% width of the available space.
          elemLayoutHints: getLayoutInHorGroupRelative(0.5),
          ...chartDef,
        },
        targetSectionId: targetSectionId || '',
      }),
    );
    setTimeout(() => {
      document.getElementById(`widgetWrapper_${chartDef.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  }

  function updateSingleChartFilter(chartId: string, newFilter: TpActiveEnSelectionFilter) {
    dispatch(
      updateChartFilter({
        dashboardDefId: dashboardDef.id,
        chartId,
        newFilter,
      }),
    );
  }

  function updateChartCustomTitle(chartId: string, newTitle: string) {
    dispatch(
      updateChartConfigParam({
        dashboardDefId: dashboardDef.id,
        chartId,
        configParamId: 'customTitle',
        value: newTitle,
      }),
    );
  }

  function updateChartCustomDescription(chartId: string, newDescription: string) {
    dispatch(
      updateChartConfigParam({
        dashboardDefId: dashboardDef.id,
        chartId,
        configParamId: 'customDescription',
        value: newDescription,
      }),
    );
  }

  function performUpdateChartConfigParam(chartId: string, configParamId: string, value: any) {
    dispatch(
      updateChartConfigParam({
        dashboardDefId: dashboardDef.id,
        chartId,
        configParamId,
        value,
      }),
    );
  }

  function performChartAspectBinding(chartId: string, aspectId: string, newColId: string) {
    dispatch(
      updateChartAspectBinding({
        dashboardDefId: dashboardDef.id,
        chartId,
        aspectId,
        newColId,
      }),
    );
  }

  function performChartCompoundAspectBinding(
    chartId: string,
    compoundAspectId: string,
    newColId: string,
    method: string,
  ) {
    dispatch(
      updateChartCompoundAspectBinding({
        dashboardDefId: dashboardDef.id,
        chartId,
        compoundAspectId,
        newColId,
        method,
      }),
    );
  }

  function addElementToSection(
    element: TpSectionElementDTO,
    dashboardDefId: string,
    targetSectionId?: string,
    ctx?: TpDashboardCreationContext,
  ) {
    const dataContextId = ctx?.dataContext.id || dataContext.id;
    dispatch(
      addNewElementToSection({
        dashboardDefId,
        element,
        targetSectionId: targetSectionId || '',
        ctx: { dataContextId, contextVariables: ctx?.contextVariables || {} },
      }),
    );
    setTimeout(() => {
      document.getElementById(`_section_${targetSectionId}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  }

  function getChartDefsFromTemplateId(chartTemplateId: string): TpChartDefinition[] {
    const compareFn = (elem: TpDashboardElement) => {
      return isChartDefinition(elem) && elem.selector === chartTemplateId;
    };
    const chartElems = findDashboardElements(dashboardDef, compareFn)
      .map((elem) => elem.elem)
      .filter(isChartDefinition);
    if (chartElems.length === 0) {
      throw new Error(`No chart found with template ID: ${chartTemplateId}`);
    }
    return chartElems;
  }

  async function getAsyncTableColumn(dataTableId: string, columnId: string) {
    try {
      return await fetchLazyColumnData(api, dataContext.id, dataTableId, columnId);
    } catch (error) {
      showErrorMessage(`Failed to obtain data: ${error}`);
      throw new Error('Data fetching failed');
    }
  }

  useEventNotification(
    DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER,
    (type: string, payload: any) => {
      if (dataContext && payload.dataContextId == dataContext.id) {
        executeDashboardTriggers(
          dashboardContext,
          dataContext,
          dispatch,
          DASHBOARD_ACTION_TRIGGERIDS.ENTITY_SELECTION_MODIFIED_BY_USER,
          payload,
        );
      }
    },
    [dashboardContext, dataContext],
  );

  useEffect(() => {
    if (!activeDashboardTemplate || !storedDashboardTemplate) {
      setTemplateError('No dashboard template provided. Please contact your administrator.');
      setShowSpinner(false);
      return;
    }
    if (Object.prototype.hasOwnProperty.call(analyticsDataRefs.dataContextRefs, assetId)) {
      const dataContext = analyticsDataStorage.dataContexts[assetId];
      setDataContextState(dataContext);
      setDataContextIsReady(true);
      if (Object.prototype.hasOwnProperty.call(analyticsDataStorage.postFetchCallBacks, assetId)) {
        analyticsDataStorage.postFetchCallBacks[assetId].callback();
        if (analyticsDataStorage.postFetchCallBacks[assetId].openEntity) {
          handleOpenItem(
            analyticsDataStorage.postFetchCallBacks[assetId].canonicalTypeId!,
            analyticsDataStorage.postFetchCallBacks[assetId].itemId!,
          );
        }
        delete analyticsDataStorage.postFetchCallBacks[assetId];
      }
    } else {
      const datacontext_id = assetInfo.properties[dashboardtemplate.property_key];
      if (!datacontext_id) {
        if (!showMissingContentPlaceholder) {
          showErrorMessage('Data context not found. \n Please contact your administrator.');
        }
        setShowSpinner(false);
        return;
      }
      api.dataContext
        .getDatacontextData(datacontext_id, assetInfo)
        .then((res) => {
          if (!res) {
            if (!showMissingContentPlaceholder) {
              showErrorMessage('Data context not found. \n Please contact your administrator.');
              throw 'Data context not found';
            }
          } else {
            return buildDataContext(res, ontology);
          }
        })
        .then((dc) => {
          if (dc) {
            updateDataContextAll(dc);
            validateDataContext(dc);
            augmentDataContext(dc);

            const newdashboardDef = createDashboardFromTemplate(
              {
                dataContext: dc,
                contextVariables: {},
              },
              storedDashboardTemplate,
              assetId,
            );
            dispatch(addDashboard({ dashboardDef: newdashboardDef }));
            analyticsDataStorage.dataContexts[dc.assetId] = dc;
            dispatch(setDataContext(dc.assetId));
            setDataContextIsReady(true);
            setDataContextState(dc);
            if (Object.prototype.hasOwnProperty.call(analyticsDataStorage.postFetchCallBacks, assetId)) {
              analyticsDataStorage.postFetchCallBacks[assetId].callback();
              if (analyticsDataStorage.postFetchCallBacks[assetId].openEntity) {
                handleOpenItem(
                  analyticsDataStorage.postFetchCallBacks[assetId].canonicalTypeId!,
                  analyticsDataStorage.postFetchCallBacks[assetId].itemId!,
                );
              }
              delete analyticsDataStorage.postFetchCallBacks[assetId];
            }
            // Load the view state if a viewStateId is present in the URL.
            // Only do this after having to initialize the data context.
            // Not when the data context was obtained from the analyticsDataStorage,
            // because in that case the view state should have already been loaded.
            initViewState(dc);
          }
          setShowSpinner(false);
        })
        .catch((error) => {
          setShowSpinner(false);
          console.error(error);
          setTemplateError(error.userError ? String(error.message) : String(error));
        });
    }
  }, []);
  useEffect(() => {
    if (dataContextIsReady) {
      const dataContextRef = analyticsDataRefs.dataContextRefs[assetId];
      const dashboardDefId = assetId;

      if (!dashboardDef) {
        throw `Invalid dashboard id: ${dashboardDefId}`;
      }
      if (dataContextRef) {
        const dashboardContext: TpDashboardContext = {
          dashboardDefId,
          dashboardInstanceId,
          dataContextStatusIndex: dataContextRef.statusIndex,
          dashboardActionDefs: dashboardDef.dashboardActionDefs,
          widgetGlobalConfig: dashboardDef.widgetGlobalConfig,
          handleOpenItem,
          addChart,
          updateChartAspectBinding: performChartAspectBinding,
          updateChartCompoundAspectBinding: performChartCompoundAspectBinding,
          updateSingleChartFilter,
          updateChartCustomTitle,
          updateChartCustomDescription,
          updateChartConfigParam: performUpdateChartConfigParam,
          addElementToSection,
          getChartDefsFromTemplateId,
          getAsyncTableColumn,
          editMode,
          elementVerticalSizeOffset: 0,
          sectionIndexContext: sectionIndexContext,
        };
        setDashboardContext(dashboardContext);
        setDashboardIsReady(true);
      }
    }
  }, [dataContextIsReady, analyticsDataRefs, editMode, dashboardDef]);

  let widgetsRendering;
  if (templateError) {
    widgetsRendering = (
      <div className={styles.error}>
        <pre>{templateError}</pre>
      </div>
    );
  }

  if (!templateError && dashboardDef && dataContextIsReady) {
    try {
      // NOTE: next line is a bit of a hack, because currently this block gets executed before dashboardContext is complete
      // Since the dashboardInstanceId is needed at widget build time, we explicitely add it here to the empty object
      // Better solution to be implemented: only build the widgets with the dashboardContext is completed and ready
      if (!dashboardContext.dashboardInstanceId) dashboardContext.dashboardInstanceId = dashboardInstanceId;
      widgetsRendering = dashboardDef.elements.map((section: TpDashboardElement, idx: number) => (
        <React.Fragment key={`W_${idx}`}>
          {renderDashboardElement(dashboardDef, section, dataContext, dashboardContext)}
        </React.Fragment>
      ));
    } catch (error: any) {
      setTemplateError(error.userError ? String(error.message) : String(error));
    }
  }

  let openedItemRendering = null;
  if (openedItem) {
    openedItemRendering = (
      <BlockingOpenedItem fraction={80} close={handleCloseItem}>
        <div style={{ height: '100%' }} ref={openedItemRef}>
          <SingleItemView
            assetInfo={assetInfo}
            dataContext={dataContext}
            dashboardContext={dashboardContext}
            canonicalTypeId={openedItem.canonicalTypeId}
            itemId={openedItem.itemId}
            closeEntityPopup={handleCloseItem}
            isAssetTemplate={isAssetTemplate}
          />
          <button className={styles.closeButton} onClick={handleCloseItem}>
            <FontAwesomeIcon icon="xmark" />
          </button>
        </div>
      </BlockingOpenedItem>
    );
  }

  function handleCloseTemplateEditor(newTemplate: string) {
    try {
      if (!storedDashboardTemplate) {
        throw new Error('No stored dashboard template found.');
      }
      const updatedTemplateInfo = { ...storedDashboardTemplate, yamlString: newTemplate };
      const newdashboardDef = createDashboardFromTemplate(
        {
          dataContext: dataContext,
          contextVariables: {},
        },
        updatedTemplateInfo,
        assetId,
      );

      dispatch(addDashboard({ dashboardDef: newdashboardDef }));
      setTemplateError(null);
    } catch (error: any) {
      setTemplateError(error.userError ? String(error.message) : String(error));
    }
  }

  function handleTemplateCopied(template: NonNullable<typeof storedDashboardTemplate>) {
    try {
      selectDashboardTemplate(template, dataContext, assetId, template.id ?? null);
      setTemplateError(null);
    } catch (error) {
      setTemplateError(error instanceof Error ? error.message : String(error));
    }
  }

  let dataContextSchema = null;
  if (dashboardIsReady) dataContextSchema = DataContextSchema({ dataContext });

  return (
    <>
      {showSpinner && !Object.prototype.hasOwnProperty.call(analyticsDataRefs.dataContextRefs, assetId) ? (
        <div className={styles.spinnerWrapper}>
          <FontAwesomeIcon icon={['fas', 'spinner']} spin className={styles.spinner} />
        </div>
      ) : (
        <>
          <div style={{ width: '100%', position: 'relative' }}>
            <div className={styles.clientArea} id="dashboardClientArea">
              <div className={styles.clientAreaScroller}>
                {widgetsRendering}
                {dashboardContext.editMode && (
                  <>
                    <div style={{ borderTop: '2px solid rgb(255,128,0)', marginTop: '80px' }} />
                    <div
                      className={stylesLayout.editToolBoxHorizontal}
                      style={{ marginTop: '40px', border: '1px dashed rgb(255,128,0)', boxSizing: 'border-box' }}
                    >
                      <button
                        onClick={() => promptAddElement(dataContext, dashboardContext, 'Dashboard', '', dispatch)}
                      >
                        Create new section...
                      </button>
                    </div>
                  </>
                )}
                <div style={{ width: '100%', height: '40px' }} ref={bottomElement} />
              </div>
              {openedItemRendering}
            </div>

            <div className={styles.leftBar}></div>
            {dashboardIsReady && (
              <TopBar
                dataContext={dataContext}
                dashboardContext={dashboardContext}
                topBarTables={dashboardDef.topbarTables?.tableSelection ?? null}
                assetInfo={assetInfo}
              />
            )}
            {!dashboardIsReady && !showSpinner && showMissingContentPlaceholder && (
              <div className={styles.dashboardPlaceholder}>{dashboardtemplate.onMissingContent.placeholder}</div>
            )}
          </div>
          {user.isOrganizationAdmin && storedDashboardTemplate && (
            <TemplateEditor
              initialTemplate={storedDashboardTemplate}
              contentModified={handleCloseTemplateEditor}
              yamlSchema={schemaDashboardTemplate}
              infoPanel={dataContextSchema}
              onUpdate={onTemplateUpdate}
              onSaveToDB={onTemplateSave}
              onTemplateCopied={handleTemplateCopied}
              canSaveAsCopy={storedDashboardTemplate.id !== getAssetTemplateId(assetId, 'dashboard')}
            />
          )}
        </>
      )}
    </>
  );
}
export default Dashboard;
